import React, { useState } from 'react';

import PhoneNumberInput from 'components/ui/phone_number_input';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Errors } from 'components/ui/errors';
import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { digitsIn, messageFor } from './helpers';
import { post } from 'helpers/api';

import type { PhoneEnrollment } from 'components/users/types';

interface Props {
  number: string;
  path: string;
  onChange: (number: string) => void;
  onClose: () => void;
  onEnrolled: (enrollment: PhoneEnrollment) => void;
}

export function PhoneForm({ number, path, onChange, onClose, onEnrolled }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Must never default to true, per Twilio compliance rules
  const [consented, setConsented] = useState(false);

  async function requestCode() {
    setBusy(true);
    setError(null);

    try {
      const pending: PhoneEnrollment = await post(path, { personalNumber: number });
      onEnrolled(pending);
    } catch (thrown) {
      setError(messageFor(thrown));
    } finally {
      setBusy(false);
    }
  }

  function handleNumberChange(value?: string) {
    onChange(value || '');
  }

  // A completeness check rather than isValidPhoneNumber: strict libphonenumber validation rejects
  // every 555 number, which is what anyone testing this will reach for, and the server's
  // phony_plausible is the real authority. PhoneNumberInput still hints on blur.
  const submittable = digitsIn(number) >= 10 && consented;

  return <>
    <Modal.Header>
      <Modal.Title>Set up texting</Modal.Title>
    </Modal.Header>

    <Modal.Body>
      <Field className="form-group">
        <Field.Label htmlFor="phoneEnrollmentNumber">Your mobile number</Field.Label>
        <PhoneNumberInput
          defaultCountry="US"
          id="phoneEnrollmentNumber"
          value={number}
          onChange={handleNumberChange}
        />
        <Field.Description>
          We&apos;ll text you a code, then give you a number to text the assistant at.
        </Field.Description>
      </Field>

      <Checkbox
        checked={consented}
        className="mt-3"
        id="phoneEnrollmentConsent"
        label="Yes, text me at this number"
        onCheckedChange={checked => setConsented(checked === true)}
      />
      <p className="text-muted small mb-0 mt-2">
        By checking the box above, you agree to receive text messages from Buildout at this
        number, including your confirmation code and replies from your assistant. Standard
        message and data rates may apply, and message frequency varies. Reply STOP to opt out
        or HELP for help. See our{' '}
        <a href="https://www.buildout.com/terms-of-use" rel="noopener noreferrer" target="_blank">
          Terms of Use
        </a>{' '}
        and{' '}
        <a href="https://www.buildout.com/terms-of-use#privacy-policy" rel="noopener noreferrer" target="_blank">
          Privacy Policy
        </a>.
      </p>

      <Errors error={error} />
    </Modal.Body>

    <Modal.Footer>
      <Button
        disabled={busy}
        label="Cancel"
        variant="ghost"
        onClick={onClose}
      />
      <Button disabled={busy || !submittable} label="Send code" onClick={requestCode} />
    </Modal.Footer>
  </>;
}
